// assetFrontend_1_bak/src/components/RolePermissions.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const RolePermissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem('token');

  // Fetch all roles on component mount
  useEffect(() => {
    fetchRoles();
    fetchAllPermissions();
  }, []);

  // Fetch role permissions when a role is selected
  useEffect(() => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole);
    }
  }, [selectedRole]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${baseUrl}/auth/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('Failed to fetch roles');
    }
  };

  const fetchAllPermissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/api/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async (roleId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/api/permissions/role/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRolePermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      setError('Failed to fetch role permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    setSuccess('');
    setError('');
  };

  const isPermissionChecked = (permissionId) => {
    return rolePermissions.some(p => p.id === permissionId);
  };

  const handlePermissionToggle = (permissionId) => {
    setRolePermissions(prev => {
      const isChecked = prev.some(p => p.id === permissionId);
      if (isChecked) {
        return prev.filter(p => p.id !== permissionId);
      } else {
        const permission = permissions.find(p => p.id === permissionId);
        return [...prev, permission];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const permissionIds = rolePermissions.map(p => p.id);

      await axios.post(
        `${baseUrl}/api/permissions/role/${selectedRole}/assign`,
        { permissionIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Permissions updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setError(error.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by resource
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {});

  const formatResourceName = (resource) => {
    return resource
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatActionName = (action) => {
    return action.charAt(0).toUpperCase() + action.slice(1);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Role Permissions Management
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Assign permissions to roles to control what users with each role can access
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="role-select-label">Select Role</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={selectedRole}
              label="Select Role"
              onChange={handleRoleChange}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedRole && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Permissions
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Select the permissions you want to assign to this role
                </Typography>

                <Grid container spacing={2}>
                  {Object.entries(groupedPermissions).map(([resource, perms]) => (
                    <Grid item xs={12} sm={6} md={4} key={resource}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            {formatResourceName(resource)}
                          </Typography>
                          <FormGroup>
                            {perms.map((permission) => (
                              <FormControlLabel
                                key={permission.id}
                                control={
                                  <Checkbox
                                    checked={isPermissionChecked(permission.id)}
                                    onChange={() => handlePermissionToggle(permission.id)}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2">
                                      {formatActionName(permission.action)}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {permission.description}
                                    </Typography>
                                  </Box>
                                }
                              />
                            ))}
                          </FormGroup>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Permissions'}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default RolePermissions;
